import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { cloneElement, useEffect, useState } from 'react';
import { compose, layout, position, space } from 'styled-system';

import Icon from '../Icon';

const baseStyles = ({ theme, active, whiteSpace }) => css`
  align-items: center;
  border-radius: ${theme.border.radius.size4};
  background-color: ${active
    ? theme.colors.tag.backgroundActive
    : theme.colors.tag.background};
  color: ${active
    ? theme.colors.tag.foregroundActive
    : theme.colors.tag.foreground};
  display: flex;
  font-weight: ${theme.typography.fontWeights.regular};
  height: fit-content;
  justify-content: center;
  user-select: none;
  white-space: ${whiteSpace};
  width: fit-content;
`;

const sizes = ({ theme, size }) => {
  const tagSizes = {
    size1: css`
      font-size: ${theme.typography.texts.size2.fontSize};
      line-height: ${theme.typography.texts.size2.lineHeight};
      padding: ${theme.space[1]} ${theme.space[4]};
    `,
    size2: css`
      font-size: ${theme.typography.texts.size3.fontSize};
      line-height: ${theme.typography.texts.size3.lineHeight};
      padding: 2px ${theme.space[2]};

      ${theme.mediaQueries.lg} {
        padding: ${theme.space[1]} ${theme.space[2]};
      }
    `,
    size3: css`
      font-size: ${theme.typography.texts.size3.fontSize};
      line-height: ${theme.typography.texts.size3.lineHeight};
      padding: 2px ${theme.space[2]};
    `,
    size4: css`
      font-size: ${theme.typography.texts.size3.fontSize};
      line-height: ${theme.typography.texts.size3.lineHeight};
      padding: 1px 3.5px;
    `,
  };

  return size && tagSizes[size];
};

const clickableStyles = ({ theme, active, clickable, isHoverActive }) =>
  clickable &&
  css`
    cursor: pointer;
    transition: ${theme.transitions.fast};

    &:hover {
      background-color: ${active
        ? isHoverActive && theme.colors.tag.background
        : isHoverActive && theme.colors.tag.backgroundHover};
      color: ${active
        ? isHoverActive && theme.colors.tag.foreground
        : isHoverActive && theme.colors.tag.foregroundHover};
    }
  `;

const typeStyles = ({ theme, type }) => {
  const tagTypes = {
    primary: css`
      background: ${theme.colors.tag.backgroundPrimary};
      color: ${theme.colors.tag.foregroundPrimary};
    `,
    alert: css`
      background: ${theme.colors.tag.backgroundAlert};
      color: ${theme.colors.tag.foregroundAlert};
    `,
    positive: css`
      background-color: ${theme.colors.tag.backgroundPositive};
      color: ${theme.colors.tag.foregroundPositive};
    `,
  };

  return type && tagTypes[type];
};

const StyledTag = styled('div')`
  ${baseStyles}
  ${sizes}
  ${clickableStyles}
  ${typeStyles}
  ${compose(position, space, layout)}
`;

const iconSizes = (size) => {
  const iconSizesMap = {
    size1: {
      padding: 'size2',
      margin: 2,
    },
    size2: {
      padding: 'size3',
      margin: 1,
    },
    size3: {
      padding: 'size3',
      margin: 1,
    },
  };

  return size && iconSizesMap[size];
};

function Tag({
  active,
  children,
  clickable,
  iconLeft,
  iconRight,
  size,
  type,
  onClick,
  ...rest
}) {
  const [isActive, setActive] = useState(active);
  const [currRightIcon, setCurrRightIcon] = useState(
    isActive ? <Icon name="check" /> : iconRight
  );
  const [isHoverActive, setHoverActive] = useState(true);

  useEffect(() => {
    if (!clickable) return;

    setCurrRightIcon(isActive ? <Icon name="check" /> : iconRight);
  }, [clickable, isActive, iconRight]);

  const onClickHandler = (e) => {
    if (!clickable) return;

    onClick(e);
    setHoverActive(false);

    setActive(false);
  };

  const onMouseOverHandler = () => {
    if (!clickable) return;

    if (isActive) {
      setCurrRightIcon(<Icon name="close" />);
    }
  };

  const onMouseOutHandler = () => {
    if (!clickable) return;

    if (isActive) {
      setCurrRightIcon(<Icon name="check" />);
    }

    setHoverActive(true);
  };

  return (
    <StyledTag
      clickable={clickable}
      active={isActive}
      type={type}
      isHoverActive={isHoverActive}
      size={size}
      onClick={onClickHandler}
      onBlur={onMouseOutHandler}
      onFocus={onMouseOverHandler}
      onMouseOver={onMouseOverHandler}
      onMouseOut={onMouseOutHandler}
      {...rest}
    >
      {iconLeft &&
        cloneElement(iconLeft, {
          size: iconSizes(size).padding,
          mr: iconSizes(size).margin,
        })}
      {children}
      {((iconRight && !iconLeft) || isActive) &&
        cloneElement(currRightIcon, {
          size: iconSizes(size).padding,
          ml: iconSizes(size).margin,
        })}
    </StyledTag>
  );
}

Tag.SIZES = ['size1', 'size2', 'size3', 'size4'];

Tag.defaultProps = {
  active: false,
  children: null,
  clickable: false,
  iconLeft: null,
  iconRight: null,
  size: 'size2',
  whiteSpace: 'pre',
  onClick: () => {},
};

Tag.propTypes = {
  /**
   * String or `Icon` to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Set active state
   */
  active: PropTypes.bool,
  /**
   * Renders a `Tag` with clickable behavior
   */
  clickable: PropTypes.bool,
  /**
   * Renders an icon on the left side
   */
  iconLeft: PropTypes.shape(),
  /**
   * Renders an icon on the right side
   */
  iconRight: PropTypes.shape(),
  /**
   * Renders a `Tag` with one of the following sizes
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3', 'size4']),
  /**
   * `white-space` css prop
   */
  whiteSpace: PropTypes.string,
  /**
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default Tag;
